import React from 'react';
import { Icon } from '../icons.jsx';
import { StatusChip, fmtDateFull, confirmDialog, bidTitle } from '../components.jsx';
import { notify } from '../modals.jsx';
import { useData } from '../data/DataProvider.jsx';
import {
  loadBidMemory, createMemoryEntry, updateMemoryEntry,
  setMemoryArchived, deleteMemoryEntry,
} from '../data/repo.js';

const { useState, useEffect, useMemo, useCallback } = React;

const KIND_META = {
  fact: { label: 'Hecho', icon: 'FileText', tone: 'sem-info' },
  learning: { label: 'Aprendizaje', icon: 'Bookmark', tone: 'sem-success' },
  decision: { label: 'Decisión', icon: 'Target', tone: 'sem-warning' },
  insight: { label: 'Insight', icon: 'Sparkles', tone: 'sem-violet' },
};
const kindMeta = (k) => KIND_META[k] || KIND_META.fact;

// ===============================================
// Memoria activa — conocimiento de Lizzi
// (auto-generada por la IA, gestionable a mano)
// ===============================================
function MemoryScreen() {
  let demoMode = false;
  try { demoMode = useData().demoMode; } catch (e) { demoMode = false; }

  const BIDS = window.MockData?.BIDS || [];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidFilter, setBidFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [view, setView] = useState('active'); // 'active' | 'archived'
  const [editing, setEditing] = useState(null); // entry object or 'new'

  const refresh = useCallback(() => {
    if (demoMode) {
      setItems([
        { id: 'd1', bid_id: BIDS[0]?.id, kind: 'fact', source: 'analysis', archived: false, content: 'El Sobre 3 puntúa 50% por fórmula de precio; el peso económico es decisivo.', created_at: new Date().toISOString() },
        { id: 'd2', bid_id: BIDS[0]?.id, kind: 'decision', source: 'chat', archived: false, content: 'Se decide presentarse con una baja del 6% para maximizar puntos sin entrar en baja temeraria.', created_at: new Date().toISOString() },
        { id: 'd3', bid_id: BIDS[1]?.id, kind: 'learning', source: 'analysis', archived: false, content: 'La solvencia técnica exige 2 proyectos similares >800k€ en los últimos 3 años.', created_at: new Date().toISOString() },
        { id: 'd4', bid_id: BIDS[1]?.id, kind: 'insight', source: 'manual', archived: true, content: 'Nota antigua archivada: el pliego de 2024 ya no aplica a este expediente.', created_at: new Date().toISOString() },
      ]);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadBidMemory(null, { includeArchived: true })
      .then((data) => setItems(data))
      .catch((e) => notify(e.message || 'No se pudo cargar la memoria activa', 'X'))
      .finally(() => setLoading(false));
  }, [demoMode, BIDS]);

  useEffect(() => { refresh(); }, [refresh]);

  const bidName = useCallback((id) => {
    const b = BIDS.find((x) => x.id === id);
    return b ? bidTitle(b) : 'Sin licitación';
  }, [BIDS]);

  const filtered = useMemo(() => items.filter((m) =>
    (view === 'archived' ? m.archived : !m.archived)
    && (bidFilter === 'all' || m.bid_id === bidFilter)
    && (kindFilter === 'all' || m.kind === kindFilter)), [items, view, bidFilter, kindFilter]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((m) => {
      const key = m.bid_id || 'none';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const activeCount = useMemo(() => items.filter((m) => !m.archived).length, [items]);
  const archivedCount = useMemo(() => items.filter((m) => m.archived).length, [items]);

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Eliminar entrada', message: '¿Eliminar definitivamente esta entrada de la memoria?', confirmLabel: 'Eliminar', tone: 'danger', icon: 'Trash' }))) return;
    if (demoMode) { setItems((s) => s.filter((m) => m.id !== id)); return; }
    try {
      await deleteMemoryEntry(id);
      setItems((s) => s.filter((m) => m.id !== id));
      notify('Entrada eliminada de la memoria', 'Check');
    } catch (e) { notify(e.message || 'No se pudo eliminar', 'X'); }
  };

  const handleArchive = async (id, archived) => {
    if (demoMode) { setItems((s) => s.map((m) => m.id === id ? { ...m, archived } : m)); return; }
    try {
      await setMemoryArchived(id, archived);
      setItems((s) => s.map((m) => m.id === id ? { ...m, archived } : m));
      notify(archived ? 'Entrada archivada — Lizzi dejará de usarla' : 'Entrada restaurada', 'Check');
    } catch (e) { notify(e.message || 'No se pudo actualizar', 'X'); }
  };

  const handleSave = async ({ id, bid_id, kind, content }) => {
    const text = (content || '').trim();
    if (!text) { notify('El contenido no puede estar vacío', 'AlertTriangle'); return; }
    if (demoMode) {
      if (id) setItems((s) => s.map((m) => m.id === id ? { ...m, kind, content: text } : m));
      else setItems((s) => [{ id: 'demo-' + Date.now(), bid_id: bid_id || null, kind, content: text, source: 'manual', archived: false, created_at: new Date().toISOString() }, ...s]);
      setEditing(null);
      return;
    }
    try {
      if (id) {
        const updated = await updateMemoryEntry(id, { kind, content: text });
        setItems((s) => s.map((m) => m.id === id ? { ...m, ...updated } : m));
        notify('Entrada actualizada', 'Check');
      } else {
        const created = await createMemoryEntry({ bid_id, kind, content: text });
        setItems((s) => [created, ...s]);
        notify('Entrada añadida a la memoria', 'Check');
      }
      setEditing(null);
    } catch (e) { notify(e.message || 'No se pudo guardar', 'X'); }
  };

  const bidsWithMemory = useMemo(() => {
    const ids = new Set(items.map((m) => m.bid_id));
    return BIDS.filter((b) => ids.has(b.id));
  }, [items, BIDS]);

  return (
    <div className="viewport-inner fade-in" style={{ padding: '28px 32px', overflow: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="page-eyebrow" style={{ margin: 0 }}>Conocimiento</div>
          <h1 className="font-display-heavy" style={{ fontSize: 26, margin: '6px 0 4px' }}>Memoria activa</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)', maxWidth: 580, lineHeight: 1.55 }}>
            Lizzi guarda aquí automáticamente los hechos, decisiones y aprendizajes de cada análisis y conversación. Puedes editarla, archivarla o crear entradas a mano si lo necesitas. Lo archivado deja de usarse como contexto.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Metric label="Activas" value={activeCount} />
          <Metric label="Licitaciones" value={bidsWithMemory.length} />
          <button className="btn btn-primary" onClick={() => setEditing('new')}><Icon.Plus size={14} /> Nueva entrada</button>
        </div>
      </div>

      {/* Toggle activas / archivadas */}
      <div style={{ display: 'flex', gap: 6, margin: '20px 0 0' }}>
        <FilterChip active={view === 'active'} onClick={() => setView('active')}>Activas ({activeCount})</FilterChip>
        <FilterChip active={view === 'archived'} onClick={() => setView('archived')}>Archivadas ({archivedCount})</FilterChip>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, margin: '14px 0 18px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Icon.Filter size={14} style={{ color: 'var(--text-4)' }} />
        <select value={bidFilter} onChange={(e) => setBidFilter(e.target.value)} className="select-soft">
          <option value="all">Todas las licitaciones</option>
          {bidsWithMemory.map((b) => <option key={b.id} value={b.id}>{bidTitle(b)}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          <FilterChip active={kindFilter === 'all'} onClick={() => setKindFilter('all')}>Todo</FilterChip>
          {Object.entries(KIND_META).map(([k, meta]) => (
            <FilterChip key={k} active={kindFilter === k} onClick={() => setKindFilter(k)}>{meta.label}</FilterChip>
          ))}
        </div>
      </div>

      {loading && <div style={{ color: 'var(--text-4)', fontSize: 13 }}>Cargando memoria…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-3)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Sparkles size={26} style={{ color: 'var(--text-3)' }} />
          </div>
          <div className="font-display-heavy" style={{ fontSize: 20, color: 'var(--text-1)' }}>
            {view === 'archived' ? 'No hay entradas archivadas' : 'Aún no hay memoria activa'}
          </div>
          <p style={{ fontSize: 14, maxWidth: 440 }}>
            {view === 'archived'
              ? 'Cuando archives entradas aparecerán aquí; puedes restaurarlas cuando quieras.'
              : 'Analiza una licitación o habla con Lizzi: los datos clave, decisiones y aprendizajes se guardarán aquí automáticamente. También puedes crear entradas a mano.'}
          </p>
          {view !== 'archived' && (
            <button className="btn btn-outline" onClick={() => setEditing('new')}><Icon.Plus size={14} /> Crear entrada</button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {groups.map(([bidId, entries]) => {
          const bid = BIDS.find((b) => b.id === bidId);
          return (
            <div key={bidId}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{bidId === 'none' ? 'Sin licitación' : bidName(bidId)}</span>
                {bid && <StatusChip status={bid.status} />}
                <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{entries.length} entrada{entries.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                {entries.map((m) => (
                  <MemoryCard
                    key={m.id}
                    entry={m}
                    onEdit={() => setEditing(m)}
                    onArchive={() => handleArchive(m.id, !m.archived)}
                    onDelete={() => handleDelete(m.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <MemoryEditor
          entry={editing === 'new' ? null : editing}
          bids={BIDS}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="card card-pad" style={{ minWidth: 96, textAlign: 'center', background: 'var(--bg-1)' }}>
      <div className="font-display-heavy" style={{ fontSize: 22 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className="chip" style={{
      cursor: 'pointer',
      background: active ? 'var(--bg-active)' : undefined,
      color: active ? 'var(--text-1)' : undefined,
      borderColor: active ? 'var(--line-3, var(--line-2))' : undefined,
    }}>{children}</button>
  );
}

function MemoryCard({ entry, onEdit, onArchive, onDelete }) {
  const meta = kindMeta(entry.kind);
  const Ico = Icon[meta.icon] || Icon.FileText;
  const SourceIco = entry.source === 'chat' ? Icon.MessageSquare : entry.source === 'manual' ? Icon.Edit : Icon.Sparkles;
  const sourceLabel = entry.source === 'chat' ? 'Desde el chat' : entry.source === 'manual' ? 'Manual' : 'Del análisis';
  return (
    <div className="card card-pad" style={{ background: 'var(--bg-1)', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative', opacity: entry.archived ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={`sem ${meta.tone}`} style={{ width: 26, height: 26, padding: 0, justifyContent: 'center', borderRadius: 8, display: 'flex', alignItems: 'center' }}><Ico size={13} /></span>
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>{meta.label}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
          <button className="btn btn-ghost btn-icon" title="Editar" onClick={onEdit} style={{ width: 24, height: 24 }}><Icon.Edit size={12} /></button>
          <button className="btn btn-ghost btn-icon" title={entry.archived ? 'Restaurar' : 'Archivar'} onClick={onArchive} style={{ width: 24, height: 24 }}>
            {entry.archived ? <Icon.Refresh size={12} /> : <Icon.Folder size={12} />}
          </button>
          <button className="btn btn-ghost btn-icon" title="Eliminar" onClick={onDelete} style={{ width: 24, height: 24 }}><Icon.Trash size={12} /></button>
        </div>
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-1)' }}>{entry.content}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-4)', marginTop: 'auto' }}>
        <SourceIco size={11} />
        <span>{sourceLabel}</span>
        <span style={{ marginLeft: 'auto' }}>{entry.created_at ? fmtDateFull(entry.created_at) : ''}</span>
      </div>
    </div>
  );
}

function MemoryEditor({ entry, bids, onClose, onSave }) {
  const [kind, setKind] = useState(entry?.kind || 'fact');
  const [content, setContent] = useState(entry?.content || '');
  const [bidId, setBidId] = useState(entry?.bid_id || '');
  const isNew = !entry;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 540 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="page-eyebrow" style={{ margin: 0, marginBottom: 6 }}>MEMORIA ACTIVA</div>
            <h3 className="font-display-heavy" style={{ fontSize: 24, margin: 0, letterSpacing: '-0.03em' }}>
              {isNew ? 'Nueva entrada' : 'Editar entrada'}
            </h3>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={15} /></button>
        </div>

        <div style={{ padding: '22px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {isNew && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>Licitación</span>
              <select value={bidId} onChange={(e) => setBidId(e.target.value)} className="select-soft">
                <option value="">Sin licitación</option>
                {bids.map((b) => <option key={b.id} value={b.id}>{bidTitle(b)}</option>)}
              </select>
            </label>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>Tipo</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(KIND_META).map(([k, meta]) => (
                <FilterChip key={k} active={kind === k} onClick={() => setKind(k)}>{meta.label}</FilterChip>
              ))}
            </div>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>Contenido</span>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe el hecho, la decisión o el aprendizaje…"
              rows={5}
              style={{
                resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit', fontSize: 14,
                padding: '12px 14px', border: '1px solid var(--line-2)', borderRadius: 10,
                background: 'var(--bg-1)', color: 'var(--text-1)',
              }}
              autoFocus
            />
          </label>
        </div>

        <div style={{ padding: '16px 28px', borderTop: '1px solid var(--line-1)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => onSave({ id: entry?.id, bid_id: bidId, kind, content })}>
            <Icon.Check size={14} /> {isNew ? 'Crear entrada' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

window.MemoryScreen = MemoryScreen;

export { MemoryScreen };
